import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// store provider moved to index.tsx
import { useAppDispatch, useAppSelector } from './hooks/reduxHooks';
import { getMuiTheme } from './theme/themes';
import { getCurrentUser } from './store/slices/authSlice';
import { authApi } from './services/authApi';

// Components
import ToastContainer from './components/ToastContainer/ToastContainer';
import Layout from './components/Layout/Layout';
import GlobalLoader from './components/GlobalLoader';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import { UserRole } from './types';
import Entities from './pages/Entities';
import EntityDetail from './pages/EntityDetail';
import UserDetail from './pages/UserDetail';
import Chat from './pages/Chat';
import SnippetGenerator from './pages/SnippetGenerator';
import SampleWebsitePage from './pages/SampleWebsitePage';
import DataProcessing from './pages/DataProcessing';
import FeaturesPage from './pages/FeaturesPage';
import ServicesPage from './pages/ServicesPage';
import AboutPage from './pages/AboutPage';
import TechStackPage from './pages/TechStackPage';
import TestimonialsPage from './pages/TestimonialsPage';
import CaseStudiesPage from './pages/CaseStudiesPage';
import ProcessPage from './pages/ProcessPage';
import RoleGuard from './components/RoleGuard';
import NotFound from './pages/NotFound';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Notifications from './pages/Notifications';
import { usePageMeta } from './hooks/usePageMeta';
import ScrollToTop from './components/ScrollToTop';

// Theme will be selected dynamically via Redux

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// App Routes Component
const AppRoutes: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  useEffect(() => {
    // If we have a token, try to get current user
    if (isAuthenticated) {
      dispatch(getCurrentUser());
    }
  }, [dispatch, isAuthenticated]);

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/tech-stack" element={<TechStackPage />} />
      <Route path="/testimonials" element={<TestimonialsPage />} />
      <Route path="/case-studies" element={<CaseStudiesPage />} />
      <Route path="/process" element={<ProcessPage />} />
      <Route path="/login" element={<Login />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="users" element={
          <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.MANAGER]}>
            <Users />
          </RoleGuard>
        } />
        <Route path="users/:userId" element={
          <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.MANAGER]}>
            <UserDetail />
          </RoleGuard>
        } />
        <Route path="entities" element={<Entities />} />
        <Route path="entities/:entityId" element={<EntityDetail />} />
        <Route path="chat" element={<Chat />} />
        <Route path="snippets" element={<SnippetGenerator />} />
        <Route path="sample-website" element={<SampleWebsitePage />} />
        <Route path="data-processing" element={
          <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.MANAGER]}>
            <DataProcessing />
          </RoleGuard>
        } />
        <Route path="profile" element={<Profile />} />
        <Route path="settings" element={<Settings />} />
        <Route path="notifications" element={<Notifications />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

// Main App Component
const App: React.FC = () => {
  const themeName = useAppSelector((state) => (state as any).theme.current) as any;
  const theme = getMuiTheme(themeName);
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', themeName);
  }
  usePageMeta('AskNow – AI Knowledge Chatbot', 'Manage AI chatbots, knowledge bases, users, and analytics – for everyone.');
  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <CssBaseline />
        <Router>
          <ScrollToTop />
          <AppRoutes />
          <ToastContainer />
          <GlobalLoader />
        </Router>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default App;
